/**
 * WebSmMissionHub — the step the live mission reaches to start its steps.
 * Never seen: a new mission follows its entry branch (`M13` → `M13 S1`);
 * a partial mission resumes its first unfinished step, and a completed
 * chain follows its final branch to the stamp. A mission with no chain goes
 * back to the live screen, where the file still opens the old way.
 */
import { useEffect } from "react";
import { useFunnelStore, useStepperStore } from "@/store";
import { useMissionProgress } from "@/store/mission-progress";
import type { StepTypeProps } from "./types";
import { chainFor, missionEvent, progressKey } from "./mission-steps";

export default function WebSmMissionHubType({ dispatch }: StepTypeProps) {
  const missionId = useFunnelStore((s) => s.missionId);
  const hasChain = useStepperStore((s) =>
    missionId == null
      ? false
      : !!s.graph?.steps[s.currentStepName]?.branches.some(
          (b) => b.eventV === missionEvent(missionId),
        ),
  );
  const goTo = useStepperStore((s) => s.goTo);
  useEffect(() => {
    if (missionId == null || !hasChain) {
      goTo("Delivered");
      return;
    }
    const graph = useStepperStore.getState().graph;
    const chain = chainFor(graph, missionId);
    const done =
      useMissionProgress.getState().done[progressKey(missionId)] ?? [];
    const last = chain.at(-1);
    const nextStep =
      chain.find((_, i) => !done.includes(i))?.stepName ??
      (last &&
        graph?.steps[last.stepName]?.branches.find((b) => b.eventV === "CLICK1")
          ?.nextStep);
    // Entering S1 would overwrite the saved progress, so resume directly.
    if (done.length && nextStep) goTo(nextStep);
    else dispatch(missionEvent(missionId));
    // on arrival only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <p className="grid h-[100dvh] place-content-center bg-black text-center font-mono text-[11px] tracking-[0.22em] text-muted-foreground uppercase">
      Opening your file…
    </p>
  );
}
